// src/pages/VotacionesPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, getDocs, orderBy, updateDoc, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import VotacionCard from '../components/VotacionCard'; // El componente que muestra cada votación
import { FaPoll, FaHistory } from 'react-icons/fa';
import './Votaciones.css';
import '../styles/comunidad.css';
import { countEligibleVoters } from '../utils/countEligibleVoters';
import { calcularEstadoVotacionEfectivo } from '../utils/votacionesUtils';

const VotacionesPage = () => {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const fraccionamientoId = userData?.fraccionamientoId || userData?.fraccionamientoTd || null;
    const role = String(userData?.rol || '').toLowerCase();
    const isAdmin = ['superadmin', 'admin', 'manager', 'plataforma', 'admin_fracc', 'administrator', 'administrador', 'platformadmin'].includes(role);

    const [votaciones, setVotaciones] = useState([]);
    const [totalParticipantes, setTotalParticipantes] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showExpired, setShowExpired] = useState(false);

    useEffect(() => {
        if (!fraccionamientoId) {
            setLoading(false);
            return;
        }

        const fetchVotaciones = async () => {
            try {
                const fraccionamientoRef = doc(db, 'fraccionamientos', fraccionamientoId);
                const fraccionamientoSnap = await getDoc(fraccionamientoRef);

                if (fraccionamientoSnap.exists()) {
                    const explicitTarget = inferTotalParticipantes(fraccionamientoSnap.data());
                    if (explicitTarget > 0) {
                        setTotalParticipantes(explicitTarget);
                    } else {
                        const fallbackTarget = await countEligibleVoters(fraccionamientoId);
                        setTotalParticipantes(fallbackTarget);
                    }
                } else {
                    const fallbackTarget = await countEligibleVoters(fraccionamientoId);
                    setTotalParticipantes(fallbackTarget);
                }

                const votacionesRef = collection(fraccionamientoRef, 'votaciones');
                const q = query(votacionesRef, orderBy('fechaCreacion', 'desc'));

                const querySnapshot = await getDocs(q);
                const updates = [];
                const listaVotaciones = querySnapshot.docs.map((docSnap) => {
                    const data = docSnap.data();
                    const estadoCalculado = calcularEstadoVotacionEfectivo(data);
                    if (isAdmin && estadoCalculado && data.estado !== estadoCalculado) {
                        updates.push(updateDoc(docSnap.ref, { estado: estadoCalculado }));
                    }
                    return {
                        id: docSnap.id,
                        ...data,
                        estado: estadoCalculado || data.estado || 'activa',
                    };
                });

                if (isAdmin && updates.length) {
                    await Promise.allSettled(updates);
                }

                // Ordenamos con prioridad a las activas y más recientes primero
                const ordered = [...listaVotaciones].sort((a, b) => {
                    const prioDiff = ordenarPorEstado(a.estado) - ordenarPorEstado(b.estado);
                    if (prioDiff !== 0) return prioDiff;
                    return obtenerTiempoVotacion(b) - obtenerTiempoVotacion(a);
                });
                setVotaciones(ordered);
            } catch (error) {
                console.error("Error al obtener votaciones: ", error);
                const fallbackTarget = await countEligibleVoters(fraccionamientoId);
                setTotalParticipantes(fallbackTarget);
            } finally {
                setLoading(false);
            }
        };

        fetchVotaciones();
    }, [fraccionamientoId, isAdmin]);

    const handleDeleted = (id) => {
        setVotaciones((prev) => prev.filter((v) => v.id !== id));
    };

    const [categoriaFiltro, setCategoriaFiltro] = useState('Todas');
    const [paginaActivas, setPaginaActivas] = useState(1);
    const [paginaCaducadas, setPaginaCaducadas] = useState(1);
    const ITEMS_PER_PAGE = 5;
    const CATEGORIAS_FILTRO = ['Todas', 'Comunidad', 'Vigilancia/Seguridad', 'Finanzas (Ingresos/Egresos)', 'Administración'];

    const handleCambiarCategoria = (cat) => {
        setCategoriaFiltro(cat);
        setPaginaActivas(1);
        setPaginaCaducadas(1);
    };

    const filtrarPorCategoria = (lista) => {
        if (categoriaFiltro === 'Todas') return lista;
        return lista.filter((v) => (v.categoria || 'Comunidad') === categoriaFiltro);
    };

    const votacionesActivas = filtrarPorCategoria(
        votaciones
            .filter((v) => v.estado !== 'vencida')
            .sort((a, b) => obtenerTiempoVotacion(b) - obtenerTiempoVotacion(a))
    );
    const votacionesCaducadas = filtrarPorCategoria(
        votaciones
            .filter((v) => v.estado === 'vencida')
            .sort((a, b) => obtenerTiempoVotacion(b) - obtenerTiempoVotacion(a))
    );

    const totalPaginasActivas = Math.ceil(votacionesActivas.length / ITEMS_PER_PAGE) || 1;
    const activasPaginadas = votacionesActivas.slice((paginaActivas - 1) * ITEMS_PER_PAGE, paginaActivas * ITEMS_PER_PAGE);

    const totalPaginasCaducadas = Math.ceil(votacionesCaducadas.length / ITEMS_PER_PAGE) || 1;
    const caducadasPaginadas = votacionesCaducadas.slice((paginaCaducadas - 1) * ITEMS_PER_PAGE, paginaCaducadas * ITEMS_PER_PAGE);

    if (loading) return <div className="loading-message">Cargando votaciones...</div>;

    return (
        <div className="votaciones-page">
            <div className={"modal-card" + (votaciones.length === 0 ? ' center-vertical' : '')}>
                <div className="modal-header">
                    <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                        <FaPoll style={{fontSize: '1.6em', opacity: 0.95}} />
                        <h2>Votaciones</h2>
                    </div>
                    <div className="header-actions">
                        { /* Botón 'Publicar Votación' removido por petición */ }
                    </div>
                </div>

                <div className="modal-body">
                    <div className="modal-content-wrapper">
                        <div className="votaciones-header comunidad-header">
                            <button className="btn-outline header-back-btn" onClick={() => navigate('/comunidad/avisos')}>
                                ← Volver
                            </button>
                            <h1>Votaciones Activas</h1>
                            {isAdmin && (
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <button 
                                        className={`btn-outline header-back-btn ${showExpired ? 'active' : ''}`}
                                        onClick={() => setShowExpired(!showExpired)}
                                        style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '14px' }}
                                    >
                                        <FaHistory /> {showExpired ? 'Ver Activas' : 'Ver Historial'}
                                    </button>
                                    <button className="btn-gradient header-create-btn" onClick={() => navigate('/comunidad/votaciones/crear')}>
                                        Crear votación
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Barra de Filtros Zen por Categoría */}
                        {votaciones.length > 0 && (
                            <div className="votaciones-categorias-bar">
                                {CATEGORIAS_FILTRO.map((cat) => {
                                    const isActive = categoriaFiltro === cat;
                                    const count = cat === 'Todas'
                                        ? votaciones.length
                                        : votaciones.filter((v) => (v.categoria || 'Comunidad') === cat).length;
                                    return (
                                        <button
                                            key={cat}
                                            type="button"
                                            className={`votaciones-cat-pill ${isActive ? 'active' : ''}`}
                                            onClick={() => handleCambiarCategoria(cat)}
                                        >
                                            {cat} {count > 0 && <span className="cat-count">({count})</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {votaciones.length === 0 ? (
                            <div className="form-section empty-state-votaciones">
                                <div className="empty-state-icon"><FaPoll /></div>
                                <h2>No hay votaciones registradas</h2>
                                <p>Crea la primera votación para conocer la opinión de los residentes</p>
                            </div>
                        ) : (
                            <>
                                <div className="votaciones-section">
                                    <h3 className="votaciones-section-title">Votaciones activas y próximas ({votacionesActivas.length})</h3>
                                    {votacionesActivas.length === 0 ? (
                                        <div className="form-section info-state">
                                            <p>Actualmente no hay votaciones activas. Consulta el apartado de caducadas para revisar el histórico.</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="votaciones-list">
                                                {activasPaginadas.map(votacion => (
                                                    <VotacionCard
                                                        key={votacion.id}
                                                        votacion={votacion}
                                                        isAdmin={isAdmin}
                                                        onDeleted={handleDeleted}
                                                        totalParticipantes={totalParticipantes}
                                                    />
                                                ))}
                                            </div>
                                            <PaginacionZen
                                                paginaActual={paginaActivas}
                                                totalPaginas={totalPaginasActivas}
                                                onCambiarPagina={setPaginaActivas}
                                            />
                                        </>
                                    )}
                                </div>

                                 {showExpired && votacionesCaducadas.length > 0 && (
                                    <div className="votaciones-section votaciones-section-expired">
                                        <h3 className="votaciones-section-title">Votaciones caducadas ({votacionesCaducadas.length})</h3>
                                        <div className="votaciones-list">
                                            {caducadasPaginadas.map(votacion => (
                                                <VotacionCard
                                                    key={votacion.id}
                                                    votacion={votacion}
                                                    isAdmin={isAdmin}
                                                    onDeleted={handleDeleted}
                                                    totalParticipantes={totalParticipantes}
                                                />
                                            ))}
                                        </div>
                                        <PaginacionZen
                                            paginaActual={paginaCaducadas}
                                            totalPaginas={totalPaginasCaducadas}
                                            onCambiarPagina={setPaginaCaducadas}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VotacionesPage;

function obtenerTiempoVotacion(v) {
    if (!v) return 0;
    const val = v.fechaCreacion;
    if (!val) return 0;
    if (typeof val.toMillis === 'function') return val.toMillis();
    if (typeof val.toDate === 'function') return val.toDate().getTime();
    if (val instanceof Date) return val.getTime();
    if (typeof val === 'number') return val;
    const parsed = new Date(val);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

function ordenarPorEstado(estado = '') {
    const prioridad = {
        activa: 0,
        programada: 1,
        vencida: 2,
    };
    return prioridad[estado.toLowerCase()] ?? 3;
}

function inferTotalParticipantes(data = {}) {
    const candidates = [
        data.residentesActivos,
        data.totalResidentes,
        data.metaParticipantes,
        data.capacidadParticipacion,
    ];

    for (const value of candidates) {
        const numeric = Number(value);
        if (Number.isFinite(numeric) && numeric > 0) {
            return Math.round(numeric);
        }
    }

    return 0;
}

const PaginacionZen = ({ paginaActual, totalPaginas, onCambiarPagina }) => {
    if (totalPaginas <= 1) return null;
    return (
        <div className="votaciones-paginacion" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginTop: '20px',
            marginBottom: '10px'
        }}>
            <button
                type="button"
                className="btn-zen-action"
                onClick={() => onCambiarPagina(Math.max(1, paginaActual - 1))}
                disabled={paginaActual === 1}
                style={{ opacity: paginaActual === 1 ? 0.4 : 1, cursor: paginaActual === 1 ? 'not-allowed' : 'pointer' }}
            >
                ← Anterior
            </button>
            <span style={{ fontSize: '0.88rem', fontWeight: '600', color: '#64748b' }}>
                Página {paginaActual} de {totalPaginas}
            </span>
            <button
                type="button"
                className="btn-zen-action"
                onClick={() => onCambiarPagina(Math.min(totalPaginas, paginaActual + 1))}
                disabled={paginaActual === totalPaginas}
                style={{ opacity: paginaActual === totalPaginas ? 0.4 : 1, cursor: paginaActual === totalPaginas ? 'not-allowed' : 'pointer' }}
            >
                Siguiente →
            </button>
        </div>
    );
};